/**
 * Phase 7-B — Verifier-issued attestation surface.
 *
 * Surfaces `POST /agents/{did}/attestations` (`02_api_spec.md §8.1`).
 *  - Pick agent DID via `client.discoverAgents` (autocomplete).
 *  - Pick verification_history row (last 50 from `/verifications/history`).
 *  - Score slider 0-100, optional rationale + tags.
 *  - On success → redirect to listing detail (best-effort if a listing exists).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';
import FormField from '../../components/FormField';
import Badge from '../../components/Badge';
import { shortAddr, uuid, scoreBucket } from '../marketplace/utils';
import type { Attestation, ListingsPage } from '@infinia/ida-sdk';
import { encodePathSegment } from '../../lib/url';

interface AgentRow {
  id: string;
  name?: string;
  modelInfo?: { name?: string };
}

interface VerificationHistoryRow {
  id: string;
  credentialType?: string;
  result?: string;
  outcome?: string;
  agentDid?: string;
  holderDid?: string;
  verifiedAt?: string;
  timestamp?: string;
}

export default function IssueAttestation() {
  const { t } = useTranslation(['marketplace', 'common']);
  const navigate = useNavigate();

  // Agent picker (autocomplete)
  const [agentQuery, setAgentQuery] = useState('');
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [agentLoading, setAgentLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentRow | null>(null);

  // Verification history
  const [history, setHistory] = useState<VerificationHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<VerificationHistoryRow | null>(null);

  // Form
  const [score, setScore] = useState(80);
  const [scope, setScope] = useState('');
  const [rationale, setRationale] = useState('');
  const [tagsCsv, setTagsCsv] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  // Discover agents (debounced server hit)
  useEffect(() => {
    const handle = window.setTimeout(async () => {
      if (!agentQuery || agentQuery.length < 2) { setAgents([]); return; }
      setAgentLoading(true);
      try {
        const q = encodeURIComponent(agentQuery);
        const res = await apiFetch<{ data: AgentRow[]; items?: AgentRow[] }>(
          `/agents/discover?q=${q}&limit=20`,
        );
        const arr = (res.data ?? res.items ?? []) as AgentRow[];
        setAgents(arr);
      } catch {
        setAgents([]);
      } finally {
        setAgentLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [agentQuery]);

  // Load verification history once.
  useEffect(() => {
    const run = async () => {
      setHistoryLoading(true);
      try {
        const res = await apiFetch<{ data?: VerificationHistoryRow[]; history?: VerificationHistoryRow[]; items?: VerificationHistoryRow[] }>(
          '/verifications/history?limit=50',
        );
        const arr = (res.data ?? res.history ?? res.items ?? []) as VerificationHistoryRow[];
        setHistory(arr);
      } catch {
        setHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    };
    void run();
  }, []);

  const filteredHistory = useMemo(() => {
    if (!selectedAgent) return history;
    return history.filter((r) => !r.agentDid || r.agentDid === selectedAgent.id);
  }, [history, selectedAgent]);

  const submit = async () => {
    setError(null);
    if (!selectedAgent) { setError(t('attestation.errors.agentRequired')); return; }
    if (!selectedHistory) { setError(t('attestation.errors.verificationRequired')); return; }
    if (!scope.trim()) { setError(t('attestation.errors.scopeRequired')); return; }
    setSubmitting(true);
    try {
      const tags = tagsCsv.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 8);
      const body = {
        verificationHistoryId: selectedHistory.id,
        score,
        rationale: rationale || undefined,
        scope: scope.trim(),
        tags: tags.length > 0 ? tags : undefined,
      };
      const att = await apiFetch<Attestation>(
        `/agents/${encodePathSegment(selectedAgent.id)}/attestations`,
        {
          method: 'POST',
          headers: { 'Idempotency-Key': uuid() },
          body: JSON.stringify(body),
        },
      );
      setToast({ kind: 'success', msg: t('attestation.toasts.issued') });
      // Try to surface a relevant listing. Best-effort: list listings filtered
      // by agent + capability scope and route to the first match. Otherwise
      // fall back to verifier history.
      try {
        const listings = await apiFetch<ListingsPage>(
          `/marketplace/listings?capability=${encodeURIComponent(scope.trim())}&limit=10`,
        );
        const match = listings.items.find((l) => l.agentDid === selectedAgent.id);
        if (match) {
          navigate(`/marketplace/listings/${match.id}`);
          return;
        }
      } catch { /* swallow */ }
      // Fallback redirect.
      navigate('/verifier/history');
      void att; // referenced for type-check
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'issue_failed';
      if (msg.toLowerCase().includes('rate')) {
        setError(t('attestation.errors.rateLimit'));
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('attestation.title')}</h1>
        <p className="mt-1 text-sm text-text-muted">{t('attestation.subtitle')}</p>
      </div>

      {toast && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-700">
          {toast.msg}
        </div>
      )}

      <div className="rounded-xl border border-border-subtle bg-white p-5 space-y-5">
        {/* Agent autocomplete */}
        <div>
          <label className="mb-1 block text-xs font-bold text-text-muted">{t('attestation.agent')}</label>
          {selectedAgent ? (
            <div className="flex items-center gap-2 rounded-lg border border-border-subtle bg-slate-50 px-3 py-2">
              <span className="material-symbols-outlined text-primary" style={{ fontSize: 18 }}>smart_toy</span>
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-bold">{selectedAgent.name ?? selectedAgent.modelInfo?.name ?? selectedAgent.id}</p>
                <p className="truncate font-mono text-[10px] text-text-faint">{selectedAgent.id}</p>
              </div>
              <button onClick={() => setSelectedAgent(null)} className="text-xs text-primary hover:underline">
                {t('common:change')}
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                value={agentQuery}
                onChange={(e) => setAgentQuery(e.target.value)}
                placeholder={t('attestation.agentSearch')}
                className="h-10 w-full rounded-lg border border-border-subtle bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              {agentQuery.length >= 2 && (
                <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-border-subtle bg-white shadow-sm">
                  {agentLoading ? (
                    <p className="px-3 py-2 text-xs text-text-faint">{t('common:loading')}</p>
                  ) : agents.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-text-faint">{t('attestation.agentResults')}: 0</p>
                  ) : (
                    agents.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => setSelectedAgent(a)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50"
                      >
                        <span className="material-symbols-outlined text-primary" style={{ fontSize: 14 }}>smart_toy</span>
                        <span className="font-bold">{a.name ?? a.modelInfo?.name ?? shortAddr(a.id, 8)}</span>
                        <span className="ml-auto truncate font-mono text-[10px] text-text-faint">{shortAddr(a.id, 6)}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Verification history */}
        <div>
          <label className="mb-1 block text-xs font-bold text-text-muted">{t('attestation.verificationHistory')}</label>
          {historyLoading ? (
            <p className="text-xs text-text-faint">{t('common:loading')}</p>
          ) : filteredHistory.length === 0 ? (
            <p className="text-xs text-text-faint">{t('attestation.verificationHistoryEmpty')}</p>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-lg border border-border-subtle bg-white">
              {filteredHistory.map((h) => {
                const checked = selectedHistory?.id === h.id;
                return (
                  <label
                    key={h.id}
                    className={`flex cursor-pointer items-center gap-2 border-b border-border-subtle px-3 py-2 text-xs last:border-0 ${checked ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                  >
                    <input
                      type="radio"
                      name="vh"
                      checked={checked}
                      onChange={() => setSelectedHistory(h)}
                    />
                    <span className="font-mono text-[10px] text-text-faint">{shortAddr(h.id, 8)}</span>
                    <span className="font-bold text-text-main">{h.credentialType ?? '—'}</span>
                    <span className="ml-auto text-[10px] text-text-faint">
                      {h.verifiedAt || h.timestamp ? new Date(h.verifiedAt ?? h.timestamp ?? '').toLocaleDateString() : '—'}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Score slider */}
        <div>
          <label className="mb-1 block text-xs font-bold text-text-muted">
            {t('attestation.score', { value: score })}
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={score}
            onChange={(e) => setScore(Number(e.target.value))}
            className="w-full"
          />
          <div className="mt-1 flex items-center gap-2">
            <Badge variant={scoreBucket(score) === 'positive' ? 'success' : scoreBucket(score) === 'neutral' ? 'warning' : 'error'}>
              {scoreBucket(score)}
            </Badge>
            <span className="text-[10px] text-text-faint">{t('attestation.scoreHint')}</span>
          </div>
        </div>

        <FormField
          label={t('attestation.scope')}
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          placeholder={t('attestation.scopePlaceholder')}
        />

        <FormField
          label={t('attestation.rationale')}
          as="textarea"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          placeholder={t('attestation.rationalePlaceholder')}
        />

        <FormField
          label={t('attestation.tags')}
          value={tagsCsv}
          onChange={(e) => setTagsCsv(e.target.value)}
        />

        {error && <p className="text-xs font-semibold text-red-500">{error}</p>}

        <div className="flex justify-end gap-2 border-t border-border-subtle pt-3">
          <button
            onClick={() => navigate('/verifier/history')}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50"
          >
            {t('attestation.cancel')}
          </button>
          <button onClick={submit} disabled={submitting} className="btn-primary text-xs disabled:opacity-50">
            {submitting ? t('attestation.submitting') : t('attestation.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
